import { useEffect } from "react";
import { useI18n } from "@/i18n";
import { CANONICAL_HOST, BRAND, OG_LOCALES, pageMeta } from "@/i18n/seo";
import { pathsByLang, routeSlug } from "@/i18n/routes";
import { jobPostingLd } from "@/i18n/jobs";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(attr, key) {
  document.head.querySelectorAll(`meta[${attr}="${key}"]`).forEach((el) => el.remove());
}

function upsertLink(rel, href, hreflang) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * SEO + social sharing head manager.
 * Public pages pass `pageKey` (metadata comes from i18n/seo.js). Private pages (auth, portal)
 * pass title/path and are marked noindex. Crawler-visible copies of these tags are baked into
 * the static HTML at build time by scripts/prerender.js.
 */
export default function Seo({ pageKey, title, description, path = "", pathByLang, noindex = false }) {
  const { lang } = useI18n();
  const meta = pageKey ? pageMeta(pageKey, lang) : null;
  const fullTitle = meta ? meta.title : `${title} | ${BRAND}`;
  const desc = meta ? meta.description : description;
  const slug = pageKey ? routeSlug(pageKey, lang) : path;
  const alternates = pageKey ? pathsByLang(pageKey) : pathByLang;
  const isPrivate = noindex || !pageKey;

  useEffect(() => {
    const url = `${CANONICAL_HOST}/${lang}${slug}`;
    document.title = fullTitle;
    document.documentElement.setAttribute("lang", lang);
    upsertMeta("name", "description", desc);
    upsertLink("canonical", url);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", BRAND);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:locale", OG_LOCALES[lang]);
    document.head.querySelectorAll('meta[property="og:locale:alternate"]').forEach((el) => el.remove());
    ["fr", "en", "es"].filter((l) => l !== lang).forEach((l) => {
      const el = document.createElement("meta");
      el.setAttribute("property", "og:locale:alternate");
      el.setAttribute("content", OG_LOCALES[l]);
      document.head.appendChild(el);
    });

    const image = meta ? meta.image : null;
    if (image) {
      upsertMeta("property", "og:image", image.url);
      upsertMeta("property", "og:image:secure_url", image.url);
      upsertMeta("property", "og:image:type", "image/jpeg");
      upsertMeta("property", "og:image:width", String(image.width));
      upsertMeta("property", "og:image:height", String(image.height));
      upsertMeta("property", "og:image:alt", meta.imageAlt);
      upsertMeta("name", "twitter:image", image.url);
      upsertMeta("name", "twitter:image:alt", meta.imageAlt);
    }
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);

    if (isPrivate) {
      upsertMeta("name", "robots", "noindex, nofollow");
    } else {
      removeMeta("name", "robots");
    }

    if (alternates) {
      ["fr", "en", "es"].forEach((l) => upsertLink("alternate", `${CANONICAL_HOST}/${l}${alternates[l]}`, l));
      upsertLink("alternate", `${CANONICAL_HOST}/fr${alternates.fr}`, "x-default");
    }

    upsertJsonLd("cs-org-ld", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: BRAND,
      url: CANONICAL_HOST,
      slogan: "Projets. Humains. Croissance.",
      telephone: "+1-418-549-6833",
      address: {
        "@type": "PostalAddress",
        streetAddress: "2828 Boul Laurier, Suite 700",
        addressLocality: "Québec",
        addressRegion: "QC",
        postalCode: "G1V 0B9",
        addressCountry: "CA",
      },
      description: "Consultants Sparks develops projects, people, teams and growth opportunities.",
      areaServed: ["CA", "US", "FR", "ES"],
    });
    upsertJsonLd("cs-website-ld", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: BRAND,
      alternateName: "Consultants Sparks",
      url: CANONICAL_HOST,
      inLanguage: ["fr-CA", "en-CA", "es-ES"],
    });

    if (pageKey === "careers") {
      upsertJsonLd("cs-jobposting-ld", jobPostingLd(lang));
    } else {
      document.getElementById("cs-jobposting-ld")?.remove();
    }
  }, [fullTitle, desc, lang, slug, alternates, meta, isPrivate, pageKey]);

  return null;
}
